import { AppSetting } from 'dependencies/appsettings';
import { UiState } from '../form/types';
import {
  StoreData, StoreDomainMeta, StoreDomainMatch, Entity, RenderMode,
  ValidationDomainMatch, ValidationMessageLevel
} from 'app/types-shared';
import { toJSON } from 'app/infrastructure/platform';
import get from 'lodash/get';
import { getValue as defaultValueGetter } from '../form/selectors';
import {
  ComponentDescriptor, ComponentMetadata, ConnectedComponentProps,
  InitialUiEngineProps, ComponentContext, ChildComponentContext
} from '../types-uischema';
import { ComponentAsyncData } from 'app/infrastructure/ui/form/types';
import { entityOperation, getById } from 'app/infrastructure/store';
import { CaseState } from 'app/area/case/types-store';
import { ValidationProps, ValidationLevel } from 'ui';
import { getStorePath } from 'app/infrastructure/store/path';
import { createSelector } from 'reselect';

// performance enhancer for immutable data
export const defaultArray: any = [];

/**
 * Filter entities marked for deletion
 *
 * @param item entity to test
 */
export const hasNoPendingDelete = (item: Entity) => item.operation !== entityOperation.REMOVE;

export const selectEntitiesOrEditingValues = (state: CaseState, props: any) => {
  if (state.renderMode === RenderMode.edit) {
   return state.editing?.values;
  } else if (state.renderMode === RenderMode.report) {
    return state.entities?.[props.hostProps().entityStoreKey];
  }
  return state.entities?.[state.selectedId];
};

export const selectProps = (_: CaseState, props) => {
  return props;
};

/**
 * Resolves a Entity from the redux store
 *
 * @param state component application state
 * @param props component props
 */
export const getCaseData = (
  state: CaseState,
  props: InitialUiEngineProps | ConnectedComponentProps,
  inclPendingDeletes?: boolean
): StoreData => {

  const caseData = createSelector(
    [selectEntitiesOrEditingValues, selectProps],
    (editingValues, selectorProps: any ) => {
      const storeKey = selectorProps.hostProps().entityStoreKey;
      const isEditing =  Array.isArray(editingValues);
      const readData = state.entities?.[storeKey] || defaultArray;
      return isEditing ? editingValues.filter(i => inclPendingDeletes || hasNoPendingDelete(i)) : readData;
    }
  )(state, props);

  return caseData;
};
/**
 * Creates a unique ID trail from the current entity.
 *
 * The ID trails is a combination of application and entity used in callbacks (further used in API calls)
 * to identify the current caller context.
 *
 * If iterating, returns the current id
 *
 * @param state component application state
 * @param props component props of the currently connected component
 */
export const getEntityContext = (state: any, props: any): ComponentContext => {

  if (props.resolveContext) {
    // iterated items will have the context pre-baked at this stage
    return props.resolveContext;
  }

  const hostProps = props.hostProps && props.hostProps() || undefined;
  const entityId = !!hostProps && hostProps.entityId;

  if (!state) {
    return { path: [], applicationId: undefined, mode: 0 };
  }

  return {
    path: [{ tblTrans: Number(entityId) }],
    applicationId: Number(hostProps && hostProps.applicationId || 0),
    mode: state.renderMode
  };
};

/**
 * Helper function to get the store path from a component descriptor and its current context.
 *
 * @returns Store path or the context path if store path can't be resolved. This happens when the descriptor doesn't have an entityTypePath.
 */
export const contextToStorePath = (componentDescriptor: ComponentDescriptor, context: ComponentContext) => {
  const { entityTypePath } = componentDescriptor || {};
  const { path } = context || {};
  return getStorePath(entityTypePath, path) || path;
};

/**
 * Return a value shape with
 * - value : number | string | boolean | Date
 * - valueItem: ComplexValue
 * - valueItems ComplexValue[]
 *
 * properties for a component
 *
 * @param state component application state
 * @param props component props of the currently connected component
 * @param overrideDataTag possibility to override the configured datatag from a container. Used in
 * scenarios for e.g data aggregation, that cannot be implemented with the default component configuration.
 */
export const getComponentValues = (
  appState: any,
  props: any,
  overrideDataTag?: string
) => {
  const getValue = props.valueSelector || defaultValueGetter;
  const caseData: StoreData = getCaseData(appState, props);
  const context = getEntityContext(appState, props);
  const { componentDescriptor } = props;

  // Fall back to using the UI context as path.
  const entity = getById(caseData, contextToStorePath(componentDescriptor, context));

  return getValue(
    entity,
    overrideDataTag || (componentDescriptor ? componentDescriptor.dataTag : undefined)
  );
};

export const resolveValidations = (
  context: ComponentContext,
  props: Partial<ConnectedComponentProps>,
  domainMetadata: StoreDomainMeta): Partial<ComponentMetadata> => {

  if (!props || !props.componentDescriptor) {
    return {};
  }

  const domainId = props.componentDescriptor.id;
  const allValidations = get(domainMetadata, `[${domainId}].validations`, []) as ValidationDomainMatch[];
  const validations = allValidations.filter(v => matchesCurrentValidationContext(context, v));
  const validationResult = validations.length && mapToValidationProps(domainId, validations);

  return {
    ...(validationResult && { validation: validationResult })
  };
};

export const mapToValidationProps = (domainId: number, validations: ValidationDomainMatch[]): ValidationProps => {
  function getLevel(level: ValidationMessageLevel): ValidationLevel {
    return level === ValidationMessageLevel.Error ?
      ValidationLevel.error :
      ValidationLevel.warning;
  }

  function getMaxLevel(levels: ValidationLevel[]): ValidationLevel {
    return Math.max(...levels) as ValidationLevel;
  }

  return {
    domainId,
    level: getMaxLevel(validations.map(v => getLevel(v.level))),
    messages: validations.map(v => ({
      level: getLevel(v.level),
      label: v.label,
      message: v.message
    }))
  };
};

export const resolveDomainMetadata =
  (context: ComponentContext, state: UiState, props: Partial<ConnectedComponentProps>): StoreDomainMeta =>
    props.renderMode === RenderMode.edit || props.hostProps().renderMode === RenderMode.edit
      ?
      get(state, 'editing.metadata.domains', {}) as StoreDomainMeta
      :
      get(state, `metadata[${context.path[0].tblTrans}].domains`, {}) as StoreDomainMeta;

export const resolveHasAnyValidations = (domainMetadata: StoreDomainMeta): { hasErrors: boolean; hasWarnings: boolean } =>
  Object.keys(domainMetadata)
    .reduce((aggr, current) => {
      aggr.hasErrors = aggr.hasErrors || (!!domainMetadata[current].validations &&
        domainMetadata[current].validations.some(v => v.level === ValidationMessageLevel.Error));
      aggr.hasWarnings = aggr.hasWarnings || (!!domainMetadata[current].validations &&
        domainMetadata[current].validations.some(v => v.level === ValidationMessageLevel.Warning));
      return aggr;
    }, {
      hasErrors: false,
      hasWarnings: false
    });

export const resolveWarnings = (domainMetadata: StoreDomainMeta) => {
  let warnings: any = [];
  Object.keys(domainMetadata).map(item => {
    !!domainMetadata[item].validations && warnings.push(domainMetadata[item].validations
      .filter(v => v.level === ValidationMessageLevel.Warning));
  });
  warnings = warnings && warnings.reduce((aggr, curr) => aggr.concat(curr), []) //merge list of warnings in a single array
    .map((item, i) => Object.assign({ key: i }, item)); //assign unique key to all rows to handle duplicate rows

  return warnings;
};

/**
 * Returns metadata properties (validationErrors and readonly attributes) for a component
 *
 * @param state component application state
 * @param props component props of the currently connected component
 */
export const getComponentMetadata = (
  state: UiState,
  props: Partial<ConnectedComponentProps>
): ComponentMetadata => {

  if (!state || !props || !props.componentDescriptor) {
    return {};
  }

  const context = getEntityContext(state, props);
  const domainMetadata = resolveDomainMetadata(context, state, props);

  const domainAttributes = domainMetadata[props.componentDescriptor.id];
  const resolvedDomainAttributes: Partial<ComponentMetadata> = {};

  if (domainAttributes && domainAttributes.readOnly) {
    resolvedDomainAttributes.readOnly = matchesCurrentContext(context, domainAttributes.readOnly);
  }
  if (domainAttributes && domainAttributes.requiredField) {
    resolvedDomainAttributes.requiredField = matchesCurrentContext(context, domainAttributes.requiredField);
  }
  if (domainAttributes && domainAttributes.hidden) {
    resolvedDomainAttributes.hidden = matchesCurrentContext(context, domainAttributes.hidden);
  }

  return {
    ...resolveValidations(context, props, domainMetadata),
    ...resolvedDomainAttributes
  };
};

/**
 * For validations, we want a validation to be returned if it matches the current context fully.
 * If we're rendering a panel on case level, context = { tblTrans: x }
 * A validation fails for an action, which has entityPath { tblTrans: x, TblAction: 4 }
 * We want this validation to be returned.
 *
 * @param currentContext Current resolveContext being rendered
 * @param match Current criteria match to check
 */
export function matchesCurrentValidationContext(currentContext: ComponentContext, match: StoreDomainMatch) {
  if (match.globalMatch) {
    return true;
  }
  const entityPaths = match.entityPaths;
  return (
    !!entityPaths &&
    entityPaths.some(
      row =>
        currentContext.path.filter(p => !p.tblTrans).every(c =>
          row.some(tablePk => {
            const key = Object.keys(tablePk)[0];
            return key && c[key] === tablePk[key];
          })
        )
    )
  );
}

/**
 * For readOnly, hide, etc. we want a match to be returned if the entityPath is fully matched by the current context.
 * Let's say we're rendering a sub-table of the action table:
 * context = { tblTrans: x, TblAction: 5, TblActionSubTable: 4 }
 * We have a hide criteria that has returned a match that goes for the whole action:
 * entityPath { tblTrans: x, TblAction: 5 }
 * We want this to match, even tho the current context is "deeper".
 *
 * @param currentContext Current resolveContext being rendered
 * @param match Current criteria match to check
 */
export function matchesCurrentContext(currentContext: ComponentContext, match: StoreDomainMatch) {
  if (match.globalMatch) {
    return true;
  }
  const entityPaths = match.entityPaths;
  return (
    !!entityPaths &&
    entityPaths.some( // at least one entitypath...
      row =>
        row.every( // ...must have a row that completely matches the current context
          tablePk => currentContext.path.some(
            c => {
              const key = Object.keys(tablePk)[0];
              return key && c[key] === tablePk[key];
            }
          )
        )
    )
  );
}

export const resolveVisibleSections = (availableSections: ComponentDescriptor[], state: CaseState, props: any): ComponentDescriptor[] => {
  return availableSections?.filter(s =>
    !getComponentMetadata(state, {
      componentDescriptor: s,
      renderMode: state.renderMode,
      resolveContext: props.resolveContext,
      hostProps: () => ({ renderMode: state.renderMode })
    }).hidden
  );
};

export const getComponentUid = (componentContext: ComponentContext): string =>
  componentContext ? toJSON({
    path: componentContext.path,
    mode: componentContext.mode,
    applicationId: componentContext.applicationId,
    primaryKey: (componentContext as ChildComponentContext).primaryKey
  }) : undefined;

export const getComponentDataKey = (descriptor: ComponentDescriptor, componentContext: ComponentContext) => {

  return toJSON({
    applicationId: componentContext.applicationId,
    path: componentContext.path,
    domainId: descriptor.id
  });
};

const selectComponentOptionsData = (state: CaseState, dataKey: string) => state.tempdata?.options?.[dataKey] ?? defaultArray;
const selectComponentCategoryOptionsData = (
  state: CaseState, dataKey: string
) => state.tempdata?.categoryOptions?.[dataKey] ?? defaultArray;

/**
 * Get temporary data stored for component, typically async-fetched **code table data**
 */
export const getComponentData = (
  state: UiState,
  descriptor: ComponentDescriptor,
  componentContext: ComponentContext
): { options: ComponentAsyncData; categoryOptions: ComponentAsyncData } => {
  const componentDataKey = getComponentDataKey(descriptor, componentContext);

  const makeSelectorForDataKey = () => createSelector(
    [selectComponentOptionsData, selectComponentCategoryOptionsData],
    (options, categoryOptions) => ({ options, categoryOptions })
  );

  const selectData = makeSelectorForDataKey();

  return selectData(state, componentDataKey);
};

/**
 * Our built-in inspector needs these extra props to render
 */
export const getInspectorPropsWhenDebugging = (state: UiState, props: InitialUiEngineProps) =>
  AppSetting.ComponentDebug ?
    ({
      // Used by our component inspector
      actions: props.actions,
      componentDescriptor: props.componentDescriptor,
      $inspectedComponent: (state as any).$inspectedComponent,
      preview: get(state, '$inspectedChanges.componentId') === get(props, 'componentDescriptor.id') && state.$inspectedChanges
    }) : ({});
